import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { findScrollParent } from './DocMinimap/utils/domUtils';

const SearchIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

/**
 * The element whose visible (viewport) box anchors the bar: the doc's scroll
 * container when there is one, otherwise the doc surface itself.
 */
const findAnchor = (editorRoot) => {
  if (!editorRoot) return null;
  return (
    findScrollParent(editorRoot) ||
    editorRoot.closest(
      '.doc-fullpage--sheet, .doc-fullpage, .library-doc-pane, .doc-sheet__panel-inner'
    ) ||
    editorRoot
  );
};

/** Fixed-position coords: top-right of the anchor's visible content area. */
const computePlacement = (anchor) => {
  if (!anchor) return null;
  const rect = anchor.getBoundingClientRect();
  const paddingRight = parseFloat(getComputedStyle(anchor).paddingRight) || 0;
  return {
    top: Math.max(8, rect.top) + 12,
    right: Math.max(8, window.innerWidth - rect.right) + Math.max(16, paddingRight),
  };
};

/**
 * Focus/select can still nudge a nested scroller even with preventScroll
 * (Chrome does this when focus leaves a contenteditable). Snapshot + restore.
 */
const focusWithoutScroll = (input, editorRoot) => {
  if (!input) return;
  const scroller = findScrollParent(editorRoot) || findAnchor(editorRoot);
  const pinned = scroller ? scroller.scrollTop : null;
  input.focus({ preventScroll: true });
  input.select();
  if (scroller != null && pinned != null && scroller.scrollTop !== pinned) {
    scroller.scrollTop = pinned;
  }
};

/**
 * Compact find bar — Ctrl/Cmd+F. Fixed to the viewport at the top-right of
 * the doc's visible area, so it floats wherever you are in the document
 * instead of living at the top of the scrolled content.
 */
export const DocFindBar = ({
  editorRootRef,
  isOpen,
  query,
  onQueryChange,
  matchCount,
  activeIndex,
  inputRef,
  barRef,
  onClose,
  onNext,
  onPrev,
}) => {
  const [placement, setPlacement] = useState(null);

  const measure = useCallback(() => {
    setPlacement((prev) => {
      const next = computePlacement(findAnchor(editorRootRef.current));
      if (!next) return prev;
      if (prev && prev.top === next.top && prev.right === next.right) return prev;
      return next;
    });
  }, [editorRootRef]);

  useEffect(() => {
    if (!isOpen) {
      setPlacement(null);
      return undefined;
    }
    measure();
    const raf = requestAnimationFrame(measure);
    // Capture-phase scroll catches any ancestor scroller moving the surface
    window.addEventListener('resize', measure);
    document.addEventListener('scroll', measure, { capture: true, passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      document.removeEventListener('scroll', measure, { capture: true });
    };
  }, [isOpen, measure]);

  // Focus the input once the bar actually mounts — never scroll to it
  const isMounted = placement != null;
  useEffect(() => {
    if (!isOpen || !isMounted) return undefined;
    const id = requestAnimationFrame(() => {
      focusWithoutScroll(inputRef.current, editorRootRef.current);
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, isMounted, inputRef, editorRootRef]);

  if (!isOpen || !placement) return null;

  const label =
    !query.trim() ? '' : matchCount === 0 ? '0/0' : `${activeIndex + 1}/${matchCount}`;

  return createPortal(
    <div
      ref={barRef}
      className="doc-find-bar"
      style={{ top: placement.top, right: placement.right }}
      role="search"
      aria-label="Find in document"
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <span className="doc-find-bar__icon">
        <SearchIcon />
      </span>
      <input
        ref={inputRef}
        className="doc-find-bar__input"
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder="Find in doc…"
        aria-label="Find in document"
        autoComplete="off"
        spellCheck={false}
      />
      <span className="doc-find-bar__count" aria-live="polite">
        {label}
      </span>
      <button
        type="button"
        className="doc-find-bar__btn"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        disabled={matchCount === 0}
        title="Previous (Shift+Enter)"
        aria-label="Previous match"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
      <button
        type="button"
        className="doc-find-bar__btn"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        disabled={matchCount === 0}
        title="Next (Enter)"
        aria-label="Next match"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <button
        type="button"
        className="doc-find-bar__btn doc-find-bar__btn--close"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        title="Close (Esc)"
        aria-label="Close find"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>,
    document.body
  );
};
